import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowRight, Briefcase, MapPin, Clock } from 'lucide-react';

const openings = [
  {
    title: 'Senior Web Developer',
    department: 'Development',
    type: 'Full-time',
    location: 'Remote / Canada',
    description: 'We\'re looking for an experienced web developer to join our team and help build amazing digital experiences.'
  },
  {
    title: 'UI/UX Designer',
    department: 'Design',
    type: 'Full-time',
    location: 'Remote / Canada',
    description: 'Join our design team to create beautiful, user-centered interfaces that delight users and drive results.'
  },
  {
    title: 'Digital Marketing Specialist',
    department: 'Marketing',
    type: 'Full-time',
    location: 'Remote / Canada',
    description: 'Help our clients grow their online presence with strategic digital marketing campaigns.'
  }
];

const benefits = [
  'Competitive salary and benefits',
  'Flexible remote work options',
  'Professional development budget',
  'Health and wellness programs',
  'Collaborative team environment',
  'Work-life balance'
];

export default function CareersPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="secondary" className="mb-6">
              <Briefcase className="h-4 w-4 mr-2" />
              Careers
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
              Join Our{' '}
              <span className="gradient-text">Team</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              We're always looking for talented individuals who are passionate about digital excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding-sm bg-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-display font-bold text-foreground">
              Why Work With Us?
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-accent/50 rounded-lg text-center"
              >
                <p className="text-foreground">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Openings */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">Open Positions</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              Current{' '}
              <span className="gradient-text">Openings</span>
            </h2>
          </motion.div>

          <div className="space-y-6 max-w-3xl mx-auto">
            {openings.map((job, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 border-border hover:border-primary/30 transition-all duration-300">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <Badge variant="secondary" className="mb-2">
                        {job.department}
                      </Badge>
                      <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                        {job.title}
                      </h3>
                      <p className="text-muted-foreground text-sm mb-3">
                        {job.description}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {job.type}
                        </span>
                      </div>
                    </div>
                    <Button variant="gradient" asChild className="shrink-0">
                      <Link to="/contact">
                        Apply Now
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-6">
              Don't See a Perfect Fit?
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              We're always interested in hearing from talented individuals. Send us your resume!
            </p>
            <Button variant="premium" size="lg" asChild className="bg-background text-foreground hover:bg-background/90">
              <Link to="/contact">
                Contact Us
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
